import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Archive, ChevronRight, Search, X } from "lucide-react";
import type { Account, ProjectSummary, StudioConfig } from "../shared/types";
import { formatTime } from "./api";
import { Logo, Mark, ProductTile, relativeTime } from "./components";
import { MakingThumb } from "./Making";
import { Composer } from "./Creation";
import { bilingual, useText } from "./i18n";

const TEXT = bilingual({
  en: {
    home: {
      preview: "Preview",
      heading: "Let’s make a video",
      archived: "Archived",
      recent: "Recent",
      find: "Find a video",
      clear: "Clear search",
      all: "All videos",
      failed: "We couldn’t load your videos.",
      retry: "Try again",
      noMatch: (query: string) => `No videos match “${query}”.`,
      noArchived: "Nothing archived.",
    },
    card: {
      example: "Example",
      versions: (n: number) => (n === 1 ? "1 version" : `${n} versions`),
      empty: "No video yet",
      working: "Working",
    },
  },
  es: {
    home: {
      preview: "Vista previa",
      heading: "Hagamos un vídeo",
      archived: "Archivados",
      recent: "Recientes",
      find: "Buscar un vídeo",
      clear: "Borrar búsqueda",
      all: "Todos los vídeos",
      failed: "No hemos podido cargar tus vídeos.",
      retry: "Reintentar",
      noMatch: (query: string) => `Ningún vídeo coincide con «${query}».`,
      noArchived: "No hay nada archivado.",
    },
    card: {
      example: "Ejemplo",
      versions: (n: number) => (n === 1 ? "1 versión" : `${n} versiones`),
      empty: "Aún sin vídeo",
      working: "Trabajando",
    },
  },
});

export function Home({
  account,
  config,
  projects,
  failed,
  onRetry,
  onCreated,
}: {
  account: Account;
  config: StudioConfig;
  projects: ProjectSummary[] | null;
  failed: boolean;
  onRetry: () => void;
  onCreated: () => void;
}) {
  const t = useText(TEXT).home;
  const location = useLocation();
  const [query, setQuery] = useState(""),
    [showArchived, setShowArchived] = useState(false);
  const shown = (projects || []).filter(
    (p) =>
      p.archived === showArchived &&
      `${p.title} ${p.brand}`.toLowerCase().includes(query.toLowerCase()),
  );
  const hasWork = !!projects?.length;
  return (
    <div className="studio page-enter">
      <header className="m-head">
        <Logo />
        {config.preview && <span className="preview-pill">{t.preview}</span>}
      </header>
      <div className="studio-top">
        <svg className="studio-rings" viewBox="0 0 1400 900" aria-hidden="true">
          {[150, 240, 384, 614, 983].map((r) => (
            <circle key={r} cx="700" cy="450" r={r} />
          ))}
        </svg>
        <h1 className="sr-only">{t.heading}</h1>
        <Composer
          key={`${account.id}:${new URLSearchParams(location.search).get("from") || "fresh"}`}
          account={account}
          config={config}
          onCreated={onCreated}
        />
      </div>
      {(hasWork || failed) && (
        <section className="recent" aria-labelledby="recent-title">
          <div className="section-head">
            <h2 id="recent-title" className="h2">
              {showArchived ? t.archived : t.recent}
            </h2>
            <div className="section-tools">
              <label className="search">
                <Search size={15} />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t.find}
                  aria-label={t.find}
                />
                {query && (
                  <button
                    type="button"
                    aria-label={t.clear}
                    onClick={() => setQuery("")}
                  >
                    <X size={13} />
                  </button>
                )}
              </label>
              <button
                className="chip"
                aria-pressed={showArchived}
                onClick={() => setShowArchived(!showArchived)}
              >
                <Archive size={15} /> {t.archived}
              </button>
              <Link to="/films" className="text-button all-link">
                {t.all} <ChevronRight size={14} />
              </Link>
            </div>
          </div>
          {failed ? (
            <div className="empty-line">
              {t.failed}
              <button className="text-button" onClick={onRetry}>
                {t.retry}
              </button>
            </div>
          ) : shown.length ? (
            <div className="vgrid">
              {shown.map((p) => (
                <ProjectCard key={p.id} project={p} />
              ))}
            </div>
          ) : (
            <div className="empty-line">
              {query ? t.noMatch(query) : t.noArchived}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

export function ProjectCard({ project: p }: { project: ProjectSummary }) {
  const t = useText(TEXT).card;
  const working = p.status === "working";
  return (
    <Link to={`/project/${p.id}`} className="vc project-card">
      <span className="vt">
        {p.cover ? (
          <img src={p.cover.poster} alt="" loading="lazy" />
        ) : (
          <span className="vt-empty">
            <Mark />
          </span>
        )}
        {working && <MakingThumb poster={p.cover?.poster} />}
        {p.sample && <span className="vt-badge">{t.example}</span>}
        {p.cover && !working && (
          <span className="vt-dur mono">{formatTime(p.cover.duration)}</span>
        )}
      </span>
      <span className="vc-title">{p.title}</span>
      <span className="vc-meta">
        <ProductTile name={p.brand || p.title} />
        <span>
          {p.brand || (p.versionCount ? t.versions(p.versionCount) : t.empty)}
        </span>
        <span aria-hidden="true">·</span>
        <span>{working ? t.working : relativeTime(p.updatedAt)}</span>
      </span>
    </Link>
  );
}
